import { useEffect, useState } from "react";
import { ActivityLogActiveIcon, EyeIcon, FilterIcon2 } from "../../../../../assets/icons";
import Button from "../../../../../common/components/xepp/button/Button";
import SearchableMultiselectDropdown from "../../../../../common/components/xepp/searchable-multiselect-dropdown/SearchableMultiselectDropdown";
import Filter from "../../../../../common/components/xepp/table-filter/table-filter-components";
import DataTable, { ColumnType } from "../../../../../common/components/xepp/table/DataTable";
import { useLazyGetCustomerForUtilityAdminQuery } from "../../../../../service/customer-service";
import styles from "./ReportedTable.module.scss";
import { FilterItem } from "../../../../../common/components/xepp/table-filter/table-filter.type";
import { formatTimestamp } from "../../../../../utils";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { Range } from "react-date-range";
import EmptyState from "../../../../../common/components/xepp/empty-state/EmptyState";
import { TablePagination } from "../../../../../common/components/xepp/table-pagination/TablePagination";
import { getErrorMessage } from "../../../../../utils/error-handler";
import ReportedStaffSideDialog from "../components/reported-staff-side-dialog/ReportedStaffSideDialog";
import { StaffReportResponse } from "../../../../../model/safety/staff-report";
import SearchInput from "../../../../../common/components/xepp/search-input/SearchInput";
import { useLazyGetStaffReportsQuery } from "../../../../../service/customersupport-service";

type ReportedFilter = {
	from?: string;
	to?: string;
	searchPhrase?: string;
	reasonIds?: string[];
};

type Props = {
	onFilterChange?: (filter: ReportedFilter) => void;
};

const ReportedTable = ({ onFilterChange }: Props) => {
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [checkedIds, setCheckedIds] = useState<string[]>([]);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [viewReportedStaffOpen, setViewReportedStaffOpen] = useState(false);
	const [selectedStaffReport, setSelectedStaffReport] = useState<StaffReportResponse>();
	const [Page, setPage] = useState(1);
	const PageSize = 15;

	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);

	const [selectedReason, setSelectedReason] = useState<string>("");

	const [dateRange, setDateRange] = useState<Range>({
		startDate: startDate,
		endDate: now,
		key: "selection",
	});
	const [appliedFilters, setAppliedFilters] = useState<{
		PaymentStatus?: string;
		Status?: string;
		DateFrom?: string;
		DateTo?: string;
		CustomerIds?: string[];
	}>({
		PaymentStatus: "",
		Status: "",
		DateFrom: undefined,
		DateTo: undefined,
		CustomerIds: undefined,
	});

	const [getCustomers, { data: customersPaged }] = useLazyGetCustomerForUtilityAdminQuery();

	const [getStaffReportsQuery, { data, isLoading, isFetching }] = useLazyGetStaffReportsQuery();

	useEffect(() => {
		const filterParams = {
			page: 1,
			pageSize: 15,
			search: debouncedSearchTerm,
		};

		getCustomers(filterParams)
			.unwrap()
			.catch(err => getErrorMessage(err.data));
	}, [debouncedSearchTerm, getCustomers]);

	const handleSelectionChange = (selectedId: string) => {
		setCheckedIds(prev => {
			const updated = prev.includes(selectedId) ? prev.filter(x => x !== selectedId) : [...prev, selectedId];

			setAppliedFilters({
				...appliedFilters,
				CustomerIds: updated.length === 0 ? undefined : updated,
			});

			return updated;
		});
	};

	const handleOpenFilter = () => {
		setIsFilterOpen(true);
	};

	const handleCloseFilter = () => {
		setIsFilterOpen(false);
	};

	const handleApplyFilter = () => {
		setAppliedFilters({
			...appliedFilters,
			DateFrom: dateRange.startDate ? dateRange.startDate.toISOString() : undefined,
			DateTo: dateRange.endDate ? dateRange.endDate.toISOString() : undefined,
		});

		setIsFilterOpen(false);
		setPage(1);
	};

	const handleResetFilter = () => {
		setIsFilterOpen(false);
		setCheckedIds([]);

		setDateRange({
			startDate: startDate,
			endDate: now,
			key: "selection",
		});

		setAppliedFilters({
			PaymentStatus: undefined,
			Status: undefined,
			DateFrom: undefined,
			DateTo: undefined,
			CustomerIds: undefined,
		});

		setPage(1);
	};

	const handleDateRangeChange = (ranges: any) => {
		setDateRange(ranges.selection);
	};

	const hideReportedStaff = () => {
		setViewReportedStaffOpen(false);
	};

	const filterItems: FilterItem[] = [
		{
			id: "date_range",
			label: "DATE RANGE",
			type: "date",
			initialDateRange: dateRange,
			onChange: handleDateRangeChange,
		},
		{
			id: "reason_for_visit",
			label: "REASON FOR VISIT",
			type: "radio",
			options: [
				{
					id: "inspection",
					label: "Inspection",
					value: "Inspection",
					isSelected: selectedReason === "Inspection",
				},
				{
					id: "meter_reading",
					label: "Meter Reading",
					value: "MeterReading",
					isSelected: selectedReason === "MeterReading",
				},
				{
					id: "routine_check",
					label: "Routine Check",
					value: "RoutineCheck",
					isSelected: selectedReason === "RoutineCheck",
				},
			],
			onChange: (value: string) => setSelectedReason(value),
		},
	];

	const columns: Array<ColumnType<StaffReportResponse>> = [
		{
			header: "Date & Time",
			accessor: "createdAt",
			render: (record: StaffReportResponse) => <span>{formatTimestamp(record.createdAt ?? "")}</span>,
		},
		{
			accessor: "user",
			header: "Name of Customer",
			render: (record: StaffReportResponse) => <span>{record.customerName}</span>,
		},
		{
			accessor: "address",
			header: "Address",
			render: (record: StaffReportResponse) => <span>{record.customerAddress}</span>,
		},
		{
			accessor: "staffMember",
			header: "Staff Member",
			render: (record: StaffReportResponse) => (
				<>{record.staffInfo ? <span>{record.staffInfo.name}</span> : <div className={styles.notAvailableContainer}>NA</div>}</>
			),
		},
		{
			accessor: "reasonForVisit",
			header: "Reason for Visit",
			render: (record: StaffReportResponse) => (
				<>{record.reason ? <span>{record.reason.name}</span> : <div className={styles.notAvailableContainer}>NA</div>}</>
			),
		},
		{
			accessor: "id",
			header: "",
			render: (record: StaffReportResponse) => (
				<Button
					leftIcon={<EyeIcon className={styles.icon} />}
					variant="text"
					text="View"
					onClick={() => {
						setViewReportedStaffOpen(true);
						setSelectedStaffReport(record);
					}}
				/>
			),
		},
	];

	// Header actions component
	const headerActions = (
		<>
			<SearchInput initialValue={searchTerm} onSearch={(value: string) => setSearchTerm(value)} placeholder="Search by customer / staff..." />
			<Button leftIcon={<FilterIcon2 className={styles.icon} />} text="Add Filter" variant="outline" type="button" onClick={handleOpenFilter} />
		</>
	);

	useEffect(() => {
		const filter: ReportedFilter = {
			from: appliedFilters.DateFrom,
			to: appliedFilters.DateTo,
			searchPhrase: debouncedSearchTerm,
			reasonIds: selectedReason ? [selectedReason] : undefined,
		};

		getStaffReportsQuery({ page: Page, pageSize: PageSize, ...filter });
		onFilterChange?.(filter);
	}, [Page, PageSize, appliedFilters.DateFrom, appliedFilters.DateTo, debouncedSearchTerm, selectedReason, getStaffReportsQuery, onFilterChange]);

	return (
		<>
			<div>
				<Filter
					filterItems={filterItems}
					onApplyFilter={handleApplyFilter}
					onResetFilter={handleResetFilter}
					onClose={handleCloseFilter}
					isOpen={isFilterOpen}
				/>
				<DataTable<StaffReportResponse>
					data={data?.items ?? []}
					loading={isLoading}
					isUpdating={isFetching}
					columns={columns}
					headerActions={headerActions}
					emptyComponent={<EmptyState icon={<ActivityLogActiveIcon />} title="No activity logs..." />}
				/>
				<TablePagination
					isLoading={isLoading}
					totalItems={data?.count ?? 0}
					itemsPerPage={PageSize}
					currentPage={data?.pageIndex ?? Page}
					onPageChange={setPage}
				/>
			</div>
			{viewReportedStaffOpen && <ReportedStaffSideDialog staffReport={selectedStaffReport!} onCloseDialog={hideReportedStaff} />}
		</>
	);
};

export default ReportedTable;
